import styles from './RegionalCostsVehicleCostContent.module.scss';
import React, {useCallback, useMemo, useState} from 'react';
import {useAppDispatch} from '@/Pages/Common/hooks/useAppDispatch';
import {
  Icon,
  IconDelete,
  IconDeleteSolid,
  IconEditSolid,
  IconOfferSolid
} from '@flixbus/honeycomb-icons-react';
import {
  Box,
  Button,
  Grid,
  GridCol,
  Heading,
  spaceHelpers
} from '@flixbus/honeycomb-react';
import {confirm} from '@/Pages/Common/Confirm';
import {useToast} from '@/Pages/Common/toasts-container';
import {useLocalUnitsContext} from '@/Pages/Common/LocalUnits';
import {Nullable} from '@/Pages/Common/types';
import {
  RegionalCostsBusTypeCost,
  RegionalCostsVehicleCost
} from '@/Pages/RegionalCosts/store/types';
import {deleteCountryVehicleTypeCost} from '@/Pages/RegionalCosts/store/thunks';
import {Table, TableAction, TableRow} from '@/components/Table';
import {initVehicleTypeCosts} from '@/components/VehicleTypeCosts/VehicleTypeCosts.data';
import {VehicleTypeCostsPanelState} from '@/components/VehicleTypeCosts/VehicleTypeCostsPanel/VehicleTypeCostsPanel.types';
import {getBaseCostsPerVehicleTypeTableConfig} from '@/components/VehicleTypeCosts/VehicleTypeCosts.utils';
import VehicleCostTable from '@/components/VehicleCosts/VehicleCostsTable/VehicleCostsTable';
import RegionalCostsVehicleTypeCostPanel from '@/Pages/RegionalCosts/components/RegionalCostsVehicleCostTab/RegionalCostsVehicleTypeCostsPanel/RegionalCostsVehicleTypeCostsPanel';
import {RegionalCostsVehicleCostContentProps} from './RegionalCostsVehicleCostContent.types';
import {
  getCostPerVehicleTypeHeaders,
  getRegionalCostsVehicleCostTableConfig
} from './RegionalCostsVehicleCostContent.data';
import RegionalCostsVehicleCostHelpBox from '../RegionalCostsVehicleCostHelpBox/RegionalCostsVehicleCostHelpBox';
import {useUpdateCountryBusCostEntryV2Mutation} from '@/Pages/RegionalCosts/hooks/useUpdateCountryBusCostEntryV2Mutation';

const RegionalCostsVehicleCostContent: React.FC<
  RegionalCostsVehicleCostContentProps
> = ({
  country,
  year,
  vehicleCost,
  vehicleTypes,
  vehicleTypeCosts,
  currency
}) => {
  const {3: space3, 5: space5} = spaceHelpers();
  const dispatch = useAppDispatch();

  const {localUnits} = useLocalUnitsContext();
  const {addToast} = useToast();

  const {updateCountryBusCostEntry} = useUpdateCountryBusCostEntryV2Mutation();
  const updateVehicleCosts = useCallback(
    async (updatedCosts: RegionalCostsVehicleCost) => {
      await updateCountryBusCostEntry(updatedCosts, country.id, year);
    },
    [country.id, updateCountryBusCostEntry, year]
  );

  const [panelState, setPanelState] = useState<VehicleTypeCostsPanelState>({
    data: null,
    loading: false,
    error: null
  });

  const vehicleCostsConfig = useMemo(
    () =>
      getRegionalCostsVehicleCostTableConfig(vehicleCost, country, localUnits),
    [localUnits, country, vehicleCost]
  );

  const handleEdit = useCallback((row: TableRow) => {
    setPanelState({
      data: row as Nullable<RegionalCostsBusTypeCost>,
      loading: false,
      error: null
    });
  }, []);

  const handleDelete = useCallback(
    (row: TableRow) => {
      confirm({
        PopupIcon: IconDelete,
        title: 'Delete this vehicle type cost?',
        message: ''
      }).then(confirmed => {
        if (confirmed) dispatch(deleteCountryVehicleTypeCost(row.id, addToast));
      });
    },
    [dispatch, addToast]
  );

  const getCostsPerVehicleTypeTableConfig = useCallback(
    (currency: string) => {
      const {headers, rows} = getBaseCostsPerVehicleTypeTableConfig(
        vehicleTypes,
        vehicleTypeCosts,
        country,
        getCostPerVehicleTypeHeaders(currency, localUnits)
      );
      const actions: TableAction[] = [
        {
          icon: IconEditSolid,
          onClick: handleEdit
        },
        {
          icon: IconDeleteSolid,
          appearance: 'danger',
          onClick: handleDelete
        }
      ];

      return {headers, rows, actions};
    },
    [
      country,
      handleDelete,
      handleEdit,
      localUnits,
      vehicleTypeCosts,
      vehicleTypes
    ]
  );

  const costsPerVehicleType = useMemo(
    () => getCostsPerVehicleTypeTableConfig(currency),
    [currency, getCostsPerVehicleTypeTableConfig]
  );

  return (
    <>
      <div className={styles.header}>
        <Heading size={3}>
          Default cost for {country.name} ({year})
        </Heading>
        <div className={styles.subtitle}>
          Here you can define country cost factor defaults.They will be used
          when you cannot provide cost factors for a certain bus partner.{' '}
        </div>
        <div className={styles.subtitle}>
          Fields marked with an * are mandatory and values must be specified at
          either regional or partner level.
        </div>
      </div>

      <section data-testid="base-costs">
        <Box>
          <Grid extraClasses="gap-10">
            <GridCol size={6}>
              <VehicleCostTable
                country={country}
                year={year}
                config={vehicleCostsConfig}
                vehicleCost={vehicleCost}
                updateFn={updateVehicleCosts}
              />
            </GridCol>

            <GridCol>
              <RegionalCostsVehicleCostHelpBox />
            </GridCol>
          </Grid>
        </Box>
      </section>

      <section data-testid="costs-per-vehicle-type">
        <Box extraClasses={space5.top}>
          <Grid extraClasses={space3.bottom}>
            <GridCol>
              <Heading size={3}>Costs per vehicle type</Heading>
            </GridCol>
            <GridCol inline>
              <Button
                onClick={() => {
                  setPanelState({
                    data: initVehicleTypeCosts,
                    loading: false,
                    error: null
                  });
                }}
              >
                <Icon InlineIcon={IconOfferSolid} aria-hidden="true" />
                Add vehicle type
              </Button>
            </GridCol>
          </Grid>

          <Table
            headers={costsPerVehicleType.headers}
            rows={costsPerVehicleType.rows}
            actions={costsPerVehicleType.actions}
            caption={
              costsPerVehicleType.rows.length === 0
                ? 'No entries specified on country level.'
                : undefined
            }
          />
        </Box>

        {/* Is used for both create and update logic */}
        <RegionalCostsVehicleTypeCostPanel
          country={country}
          year={year}
          currency={currency}
          vehicleTypes={vehicleTypes}
          localUnits={localUnits}
          panelState={panelState}
          setPanelState={setPanelState}
        />
      </section>
    </>
  );
};

export default React.memo(RegionalCostsVehicleCostContent);
